/**
 * Agenda publicidade fora do caminho crítico: idle + opcional interação.
 */

export const AD_DEFER_IDLE_IAB_MS = 2000;
export const AD_DEFER_IDLE_FOOTER_MS = 2500;
export const AD_DEFER_IDLE_MEDIAG_MS = 2500;
export const AD_DEFER_INTERACTION_FALLBACK_MS = 5000;

function runWhenIdle(fn, idleTimeout) {
  if (typeof window === 'undefined') return;
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => fn(), { timeout: idleTimeout });
  } else {
    setTimeout(fn, 1);
  }
}

/**
 * Agenda `fn` num slice idle. Devolve cancelamento para cleanup React.
 * @param {() => void} fn
 * @param {{ idleTimeout?: number, waitForInteractionFirst?: boolean, interactionFallbackMs?: number }} [options]
 */
export function scheduleDeferredAdLoad(fn, options = {}) {
  const idleTimeout = options.idleTimeout ?? 2500;
  const waitForInteractionFirst = Boolean(options.waitForInteractionFirst);
  const interactionFallbackMs =
    options.interactionFallbackMs ?? AD_DEFER_INTERACTION_FALLBACK_MS;

  if (typeof window === 'undefined') {
    return () => {};
  }

  let cancelled = false;

  const run = () => {
    if (cancelled) return;
    runWhenIdle(() => {
      if (!cancelled) fn();
    }, idleTimeout);
  };

  if (!waitForInteractionFirst) {
    run();
    return () => {
      cancelled = true;
    };
  }

  const events = ['pointerdown', 'keydown', 'scroll'];
  const listenerOpts = { passive: true, capture: true };
  let cleaned = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let fallbackTimer;

  const removeListenersAndTimer = () => {
    if (cleaned) return;
    cleaned = true;
    if (fallbackTimer !== undefined) clearTimeout(fallbackTimer);
    events.forEach((ev) => {
      window.removeEventListener(ev, onInteraction, listenerOpts);
    });
  };

  const onInteraction = () => {
    removeListenersAndTimer();
    run();
  };

  fallbackTimer = setTimeout(() => {
    removeListenersAndTimer();
    run();
  }, interactionFallbackMs);

  events.forEach((ev) => {
    window.addEventListener(ev, onInteraction, listenerOpts);
  });

  return () => {
    cancelled = true;
    removeListenersAndTimer();
  };
}
